<script setup lang="ts">
import {
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  toRaw,
  watch,
} from "vue";
import Toaster from "@/components/canvas/Toaster.vue";
import CanvasLayout from "@/components/canvas/CanvasLayout.vue";
import SaveOverlay from "@/components/canvas/SaveOverlay.vue";
import AnalysisFramesOverlay from "@/components/canvas/analysis/AnalysisFramesOverlay.vue";
import ObservationFramesOverlay from "@/components/canvas/analysis/ObservationFramesOverlay.vue";
import AnalysisScanOverlay from "@/components/canvas/analysis/AnalysisScanOverlay.vue";
import MergeConflictOverlay from "@/components/canvas/merge/MergeConflictOverlay.vue";

import { localDrawingRepository } from "@/repositories/local-drawing.repository";
import { Facade, InputMode } from "@/drawing";
import Settings from "@/stores/Settings";
import { disposeBootstrap } from "../utils/bootstrap.utils";
import CanvasSettingsState from "@/stores/CanvasSettingsState";
import DraggerMenuState from "@/stores/DraggerMenuState";
import { Logger } from "@/logger";
import UserState from "@/stores/UserState";
import { keyHandler } from "@/components/canvas/KeyHandler";
import { useCanvasDrop } from "@/composable/useCanvasDrop";
import { drawingImageRepository } from "@/repositories/drawingImage.repository";
import { oplogCapture } from "@/services/oplogCapture";
import { useDrawingStore } from "@/stores/fileStore";
import { AVAILABLE_FONTS } from "@/config/fonts";
import $ls, { LocalStorageKey } from "@/utils/local-storage";
import { LoadingTimer } from "@/utils/LoadingTimer";
import {
  initSaveController,
  cleanupSaveController,
  SaveState,
  waitForPendingDiscard,
} from "@/stores/SaveDrawingState";
import { useSaveReminder } from "@/composable/useSaveReminder";
import { useMemoryWarning } from "@/composable/useMemoryWarning";
import { useCanvasUiScale } from "@/composable/useCanvasUiScale";
import { DrawingWebDisabledError } from "@/drawing/managers/FileManager";
import { canvasTopPx } from "@/stores/CanvasLayoutState";
import { themeBackgroundHex } from "@/utils/planBackground";
import { isApplyingPlanBackground } from "@/stores/PlanBackgroundState";
import CanvasBusyOverlay from "@/components/canvas/CanvasBusyOverlay.vue";
import {
  disposePointerTracking,
  initPointerTracking,
  isFullscreen,
  startCanvasPointer,
} from "@/stores/CanvasViewState";

const props = defineProps<{
  drawingId: number;
}>();

CanvasSettingsState.initialize(props.drawingId, UserState.getLegends());
useSaveReminder();
// La route canvas vit hors de AuthLayout (cf. router.ts) : sans ça le sondage
// mémoire s'arrête à l'ouverture du dessin, précisément là où la mémoire monte.
useMemoryWarning();
// Taille des barres de l'outil. Le canvas suit ensuite sa zone de lui-même
// (cf. canvasSizeObserver).
useCanvasUiScale();

const drawingStore = useDrawingStore();

// Back path for native navigation (works even when JS is blocked)
const backPath = $ls.getItem(LocalStorageKey.BACK_PATH) || "/";

const isLoading = ref(false);
const loadingStatus = ref("");
let isCancelled = false;
// Créé après le chargement (hors du setup) : arrêté à la main au démontage.
let stopStylusWatch: (() => void) | null = null;
const canvas = ref<HTMLCanvasElement>();

/**
 * Record a timing step (for console logs only).
 */
function logStep(stepName: string) {
  LoadingTimer.step(stepName);
}

/**
 * Update the loading status shown to the user.
 */
function setStatus(message: string) {
  loadingStatus.value = message;
}

/**
 * Yield to the event loop to allow pending events (like clicks) to be processed.
 */
function yieldToEventLoop(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

/**
 * Load available fonts into the virtual filesystem and register with ODA.
 * This must be called after Facade.initialize() so the module is ready.
 */
async function loadFonts() {
  for (let i = 0; i < AVAILABLE_FONTS.length; i++) {
    if (isCancelled) return;
    const font = AVAILABLE_FONTS[i];
    setStatus(`Chargement des polices (${i + 1}/${AVAILABLE_FONTS.length})...`);
    try {
      await Facade.textStyles.loadFontFromURL(font.url, font.filename);
    } catch(e) {
      if (e instanceof Error) {
        Logger.warn(
          `Could not load font ${font.filename}: ${e.name} : ${e.message}`
        );
      }
    }
    await yieldToEventLoop();
  }
}

async function initializeViewer() {
  LoadingTimer.start();
  Logger.info(`DrawingCanvas.vue : start init viewer`);

  if (!canvas.value) return;

  // Step 1: Initialize ODA
  setStatus("Initialisation...");
  logStep("Facade.initialize");
  await Facade.initialize(canvas.value);

  await yieldToEventLoop();
  if (isCancelled) return;

  // Step 2: Load fonts (setStatus is called inside loadFonts for progress)
  logStep(`Load fonts (${AVAILABLE_FONTS.length})`);
  await loadFonts();

  await yieldToEventLoop();
  if (isCancelled) return;

  // Step 3: Apply settings
  logStep("Apply theme settings");
  Facade.view.setBackgroundColor(
    themeBackgroundHex(toRaw(Settings.actviveTheme)),
  );

  if (toRaw(Settings.onlyStylusOn)) {
    Facade.interaction.setInputMode(InputMode.StylusOnly);
  }
  // Réglé depuis le volet Paramètres pendant le dessin : appliqué en direct
  // (menus d'outils et règles suivent déjà le réglage).
  stopStylusWatch = watch(
    () => Settings.onlyStylusOn,
    (on) => {
      if (!Facade.isInitialized) return;
      Facade.interaction.setInputMode(
        on ? InputMode.StylusOnly : InputMode.Normal,
      );
    },
  );

  if (!props.drawingId) {
    return;
  }

  if (!Number.isInteger(props.drawingId)) {
    Logger.error(`Cannot open drawing with id ${props.drawingId}`);
    return;
  }

  // Step 4: Load drawing data from IndexedDB
  setStatus("Chargement des données...");
  logStep("IndexedDB: fetch drawing");
  const drawingId = Number.parseInt(`${props.drawingId}`, 10);
  const drawing = await localDrawingRepository.getLastSaveOrDraft(drawingId);
  logStep("IndexedDB: fetch images");
  const images = await drawingImageRepository.getByDrawingId(drawingId);

  await yieldToEventLoop();
  if (isCancelled) return;

  if (drawing) {
    // Step 5: Open DWG file
    setStatus("Ouverture du dessin...");
    logStep("Facade.file.open");
    try {
      await Facade.file.open(`drawing_${drawingId}.dwg`, drawing.binary);
    } catch(e) {
      if (e instanceof DrawingWebDisabledError) {
        // Protection Server served a disabled DrawingWeb.js — cache already cleared.
        // Store a toast key and navigate back so the user can refresh.
        Logger.captureException(e, { drawingId });
        sessionStorage.setItem("pendingToast", "drawing.write-disabled");
        window.location.href = backPath;
        return;
      }
      throw e;
    }

    await yieldToEventLoop();
    if (isCancelled) return;

    // Step 6: Load images
    if (images.length > 0) {
      setStatus("Chargement des images...");
    }
    const preparedImages = images
      .filter((img) => img.data)
      .map((img) => ({
        path: img.rasterPath,
        array: img.data,
      }));
    logStep(`loadReferencedImages (${preparedImages.length})`);
    Facade.images.loadReferencedImages(preparedImages);
    Facade.images.reloadImageDefs();
  } else {
    setStatus("Création du dessin...");
    logStep("Facade.file.createNew");
    await Facade.file.createNew();
  }

  if (isCancelled) return;

  // Oplog : capture prête (dessin existant OU neuf vide), drawingId connu.
  oplogCapture.start(drawingId);

  // Step 7: Apply lineweight BEFORE first render (no regenAll, avoids double render)
  // DraggerMenuState is already initialized via CanvasSettingsState.initialize()
  logStep("Apply lineweight (before render)");
  try {
    Facade.setLineweightDisplayInitial(
      DraggerMenuState.isLwActive
    );
  } catch(e) {
    Logger.warn(
      `Could not set initial lineweight display, will apply after render: ${e}`
    );
  }

  // Step 8: Finalize - resize and first render
  setStatus("Affichage...");
  logStep("view.resize");
  Facade.view.resize();
  logStep("view.update (first render)");
  Facade.view.update();
  logStep("view.regenerate");
  Facade.view.regenIfNeeded();
  logStep("First render complete");
  Facade.snap.warmUp();
  logStep("snap warmed up");

}

/**
 * Cancel loading and go back using native navigation.
 * Uses window.location.href for immediate navigation.
 */
function cancelAndGoBack() {
  isCancelled = true;
  // Force immediate navigation using native browser navigation
  window.location.href = backPath;
}

// Handle window resize
function onResize() {
  if (Facade.isInitialized) {
    Facade.view.resize();
    Facade.view.update();
    // Les règles ne se redessinent que sur un changement de vue (zoom, pan) :
    // sans ce rappel, elles restent calées sur l'ancienne taille du canvas quand
    // il grandit (rotation de la tablette, changement de taille d'interface).
    Facade.rulers.update();
  }
}

// La zone du canvas bouge sans que la fenêtre change : en-tête sur 1 ou 2
// lignes, ligne des notes repliée, plein écran, taille de l'interface… Un seul
// observateur sur le canvas couvre tous ces cas, rotation de la tablette
// comprise. Appelé avant l'affichage : le viewer n'est jamais étiré une image.
const canvasSizeObserver = new ResizeObserver((entries) => {
  const box = entries[0]?.contentRect;
  if (!box || box.width === 0 || box.height === 0) return;
  onResize();
});

// Prevent leaving page with unsaved changes (browser close/refresh)
function onBeforeUnload(e: BeforeUnloadEvent) {
  if (SaveState.value === "changed" || SaveState.value === "saving") {
    e.preventDefault();
    e.returnValue = "";
    return "";
  }
}

onMounted(async () => {
  // La liste des dessins locaux relit toute la table après chaque écriture,
  // donc après chaque sauvegarde de ce dessin : coût inutile ici, et au pire
  // moment (cf. fileStore.suspendLocalWatch). Reprise au démontage.
  drawingStore.suspendLocalWatch();

  isLoading.value = true;
  loadingStatus.value = "Préparation...";

  // Avant tout `await` : le suivi des pointeurs doit être en place dès le premier
  // geste, sans attendre le chargement du viewer.
  initPointerTracking();

  // Wait for UI to render and be interactive before starting heavy operations
  // This gives the user time to click "back" if they opened the wrong drawing
  await new Promise((resolve) => setTimeout(resolve, 300));

  if (isCancelled) return;

  await initializeViewer();

  if (isCancelled) return;

  // Initialize save controller (must be before any drawing operations)
  initSaveController();

  // Initialize settings (lineweight already applied before first render)
  CanvasSettingsState.onDrawingOpen({ skipLineweight: true });

  logStep("keyHandler.init");
  keyHandler.init();

  // Resize the viewer whenever its area changes (see canvasSizeObserver)
  if (canvas.value) canvasSizeObserver.observe(canvas.value);

  // Add unsaved changes warning (browser close/refresh)
  window.addEventListener("beforeunload", onBeforeUnload);

  logStep("Loading complete");
  LoadingTimer.end();

  // Hide loader
  isLoading.value = false;
});

onBeforeMount(() => {
  // disable overscroll when canvas is shown
  document.body.classList.add("body-no-overscroll");
});

onBeforeUnmount(async () => {
  // Cancel any ongoing loading
  isCancelled = true;
  stopStylusWatch?.();

  // Cleanup save controller (cancels pending debounced saves)
  cleanupSaveController();

  // Oplog : couper la capture.
  oplogCapture.stop();

  // Déclenche une synchro immédiate en quittant le canvas, plutôt que d'attendre
  // le prochain tick du moniteur (jusqu'à 10s) — c'est juste après avoir fermé un
  // dessin que l'utilisateur a le plus de chances de fermer l'onglet dans la foulée.
  // Le save (CanvasExitDialog) est déjà awaité à cet instant, mais le discard (CanvasExitDialog
  // "Abandonner" / guard router) est fire-and-forget — on attend sa purge avant
  // de synchroniser, sinon bindPendingToRevision/markLocallySaved peuvent
  // rattacher des events que l'utilisateur vient d'abandonner (cf. DR-DRAWING-Y1
  // / MR). triggerSync() (plutôt que syncDrawings() en direct) partage le verrou
  // de syncMonitor avec AuthLayout.startAutoSync(), qui se déclenche quasiment en
  // même temps en remontant sur la route précédente.
  // Reprise AVANT tout await : une purge d'abandon qui traînerait ne doit pas
  // laisser la liste des dessins locaux figée après la sortie de l'outil.
  drawingStore.resumeLocalWatch();
  await waitForPendingDiscard();
  void drawingStore.triggerSync();

  // re-enable scrolling
  document.body.classList.remove("body-no-overscroll");
  disposeBootstrap();

  canvasSizeObserver.disconnect();

  disposePointerTracking();

  // Remove unsaved changes warning
  window.removeEventListener("beforeunload", onBeforeUnload);

  try {
    // No save here — CanvasExitDialog handles all save cases
  } finally {
    if (Facade.isInitialized) {
      Facade.dispose();
    }
  }
  keyHandler.dispose();
});

const { dropRef } = useCanvasDrop(canvas);
</script>

<template>
  <!--@touchstart.stop.prevent prevents the default doubletap hold behavior-->
  <div class="h-100 w-100">
    <CanvasLayout v-if="!isLoading" :drawingId="props.drawingId" />
    <div
      class="canvas-container position-absolute"
      :style="
        isFullscreen
          ? { top: 0, left: 0, right: 0, bottom: 0 }
          : {
              // Les barres sont zoomées (cf. useCanvasUiScale) : le canvas
              // commence là où elles s'arrêtent réellement (px écran).
              top: `${canvasTopPx}px`,
              // La barre d'outils (à gauche) et les onglets des volets (à
              // droite) flottent sur le plan.
              left: 0,
              right: 0,
              bottom: 0,
            }
      "
      @touchstart.prevent
      @pointerdown="startCanvasPointer"
      ref="dropRef"
    >
      <canvas ref="canvas" id="dr-canvas" />
      <!-- Cadres de l'analyse du plan — même géométrie que le canvas -->
      <AnalysisFramesOverlay v-if="!isLoading" />
      <!-- Cadres des observations réelles (onglet Observations du volet) -->
      <ObservationFramesOverlay v-if="!isLoading" />
      <!-- Balayage lumineux pendant l'analyse (algo + passes IA) -->
      <AnalysisScanOverlay v-if="!isLoading" />
      <!-- Conflit de fusion courant (volet de fusion) : cadre + légende -->
      <MergeConflictOverlay v-if="!isLoading" />
    </div>
  </div>

  <!-- Fullscreen loading overlay - direct render, no teleport -->
  <CanvasBusyOverlay v-if="isLoading" :status="loadingStatus">
    <a
      :href="backPath"
      class="btn btn-outline-light"
      @click="cancelAndGoBack"
    >
      <i class="bi bi-chevron-left me-2"></i>
      {{ $t("back") }}
    </a>
  </CanvasBusyOverlay>
  <!-- Fond du plan changé depuis la modale Affichage : le plan est redessiné -->
  <CanvasBusyOverlay
    v-if="isApplyingPlanBackground"
    :status="$t('canvas:display.planBackground.applying')"
  />

  <Toaster />
  <SaveOverlay />
</template>

<style>
.body-no-overscroll {
  overscroll-behavior: none;
  overflow: hidden;
  position: fixed;
}

.canvas-container {
  touch-action: none;
}

#dr-canvas {
  width: 100%;
  height: 100%;
  display: block;
}
</style>
